import React, { useState } from "react";
import BoxSvg from "../../assets/openbox.svg";
import longhorizontal from "../../assets/longhorizontal.svg";
import longvertical from "../../assets/longvertical.svg";
import Rectangularprism from "../../assets/retangularprism.png";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import * as Utils from "../../Utils";
import "./InfoBox.css";

const InfoBox = ({ selectedBox, metrics = 1, onAfterSave }) => {
  const [isEditing, setIsEditing] = useState(false);

  const { nomenclature, nsn, niin, weight, width, height, depth, position_no } = selectedBox || {};

  const [dimensions, setDimensions] = useState({
    Width: width,
    Length: depth,
    Depth: height,
  });

  React.useEffect(() => {
    if (selectedBox) {
      setDimensions({ Width: width, Length: depth, Depth: height });
    }
  }, [width, height, depth, selectedBox]);

  if (!selectedBox) return (
    <div className="info-box-default">
      <div className="default-text">
        <img src={BoxSvg} alt="Box Icon" className="infobox-icon" />
        <div className="infobox-title">Box Information</div>
        <div className="infobox-smalltext">Select a box to see more info about it.</div>
      </div>
    </div>
  );

  function returnIcon() {
    if (width >= depth) {
      return <img src={longhorizontal} className="instructions-icon" />;
    } else {
      return <img src={longvertical} className="instructions-icon" />;
    }
  }
  function returnOverlapIcon() {
    if (niin === 999999999) {
      return <SpaceDashboardIcon className="instructions-icon" />;
    }
  }
  const handleSave = async () => {
    try {
      const placedBoxId = selectedBox && selectedBox.id;
      if (!placedBoxId) {
        console.error("InfoBox.handleSave: no placed_boxes.id on selectedBox", selectedBox);
        alert("Cannot save: this box does not have a database id yet.");
        return;
      }

      // Map the InfoBox UI labels to placed_boxes columns:
      //   dimensions.Width  -> width  (X axis)
      //   dimensions.Length -> length (Z axis)
      //   dimensions.Depth  -> height (Y axis)
      const newDims = {
        width: Number(dimensions.Width),
        length: Number(dimensions.Length),
        height: Number(dimensions.Depth),
      };

      if (![newDims.width, newDims.length, newDims.height].every((v) => Number.isFinite(v) && v > 0)) {
        alert("All dimensions must be positive numbers.");
        return;
      }

      const result = await Utils.updatePlacedBoxDimensions(placedBoxId, newDims);

      if (result && Array.isArray(result.warnings) && result.warnings.length > 0) {
        const lines = result.warnings.map((w) => `- ${w.message || w.code}`).join("\n");
        console.warn("[InfoBox.handleSave] warnings:", result.warnings);
        // Non-blocking: inform the user but continue to refresh.
        alert(`Saved with warnings:\n${lines}`);
      }

      setIsEditing(false);

      // After the dimension edit is persisted to placed_boxes, ask the parent
      // to re-run the ISU-90 quadrant placement so the canvas reflects the
      // new layout. Parent decides whether this is applicable (ISU-90 only).
      if (typeof onAfterSave === "function") {
        try {
          await onAfterSave();
        } catch (e) {
          console.error("onAfterSave (quadrant refresh) failed:", e);
        }
      }
    } catch (error) {
      console.error("Failed to save:", error);
      const apiMsg = error && error.response && error.response.data && error.response.data.message;
      alert(`Failed to save: ${apiMsg || error.message || error}`);
    }
  };

  const infoLines =
    metrics === 1
      ? [
          `Name: ${nomenclature}`,
          `NSN: ${nsn}`,
          `Weight: ${weight} kg`,
        ]
      : [
          `Nomenclature: ${nomenclature}`,
          `NIIN: ${niin}`,
          `NSN: ${nsn}`,
          `Weight: ${weight} lb`,
        ];

  const unit = metrics === 1 ? "cm" : "inches";
  return (
    <div className="info-box">
      <div className="position">
        <div>Box Label | Position #{position_no} </div>
        <div className="icon-action" style={{ cursor: 'pointer' }}>
          {isEditing ? (
            <SaveIcon onClick={handleSave} sx={{ width: 15, height: 15 }}/>
          ) : (
            <EditIcon onClick={() => setIsEditing(true)} sx={{ width: 15, height: 15 }}/>
          )}
        </div>
      </div>
      <div className="instructions">
        <div>Instructions</div>
        <img src={Rectangularprism} className="instructions-icon" />
        {returnIcon()}
        {returnOverlapIcon()}
      </div>
      <div className="info-text">
        {/* Keep static lines like Nomenclature and NSN as they are */}
        {infoLines.map((line, idx) => (
          <div key={idx}>{line}</div>
        ))}

        {/* Render Editable Dimensions */}
        {isEditing ? (
          <div className="edit-fields">
            {Object.keys(dimensions).map((field) => (
              <div key={field} style={{ marginBottom: '5px' }}>
                <label>{field}: </label>
                <input
                  type="number" // Changed to number for easier input
                  value={dimensions[field]}
                  onChange={(e) =>
                    setDimensions({ ...dimensions, [field]: e.target.value })
                  }
                  style={{ height:'10px', width: '50px' }}
                /> {unit}
              </div>
            ))}
          </div>
        ) : (
          <>
            <div>Width: {dimensions.Width} {unit}</div>
            <div>Length: {dimensions.Length} {unit}</div>
            <div>Depth: {dimensions.Depth} {unit}</div>
          </>
        )}
      </div>
    </div>
  );
};

export default InfoBox;
